import rawTypographyTokens from "./tokens.json";
import rawColorTokens from "./colorTokens.json";
import globalColors from "./globalColors.json";

const globalColorPalette = Object.fromEntries(
  Object.entries(globalColors?.GlobalColors || {}).map(([family, shades]) => [
    family,
    Object.fromEntries(
      Object.entries(shades || {}).map(([shade, config]) => [
        shade,
        config?.value,
      ])
    ),
  ])
);

const getTokenNode = (path, source = rawTypographyTokens) => {
  if (!Array.isArray(path)) {
    return (path || "")
      .split(".")
      .reduce((acc, key) => (acc ? acc[key] : undefined), source);
  }
  return path.reduce((acc, key) => (acc ? acc[key] : undefined), source);
};

const resolveValue = (value, source = rawTypographyTokens) => {
  if (
    typeof value === "string" &&
    value.startsWith("{") &&
    value.endsWith("}")
  ) {
    const tokenPath = value.slice(1, -1);
    if (tokenPath.startsWith("GlobalColors.")) {
      const [, family, shade] = tokenPath.split(".");
      return globalColorPalette[family]?.[shade] ?? value;
    }
    const node = getTokenNode(tokenPath, source);
    if (!node) return value;
    return resolveValue(node?.value ?? node, source);
  }
  return value;
};

const resolveObject = (obj, source = rawTypographyTokens) =>
  Object.fromEntries(
    Object.entries(obj || {}).map(([key, val]) => [
      key,
      typeof val === "object" && val !== null
        ? resolveObject(val, source)
        : resolveValue(val, source),
    ])
  );

const resolveTypographyToken = (...path) => {
  const node = getTokenNode(path, rawTypographyTokens);
  if (!node) return {};
  return resolveObject(node.value || node, rawTypographyTokens);
};

const resolveColorToken = (...path) => {
  const node = getTokenNode(path, rawColorTokens);
  if (!node) return undefined;
  return resolveValue(node?.value ?? node, rawColorTokens);
};

const displayPrimary = resolveTypographyToken("Display", "Display 1");
const headingDesktopH1 = resolveTypographyToken("Heading", "Desktop", "H1");
const headingDesktopH2 = resolveTypographyToken("Heading", "Desktop", "H2");
const headingDesktopH3 = resolveTypographyToken("Heading", "Desktop", "H3");
const headingDesktopH4 = resolveTypographyToken("Heading", "Desktop", "H4");
const headingDesktopH5 = resolveTypographyToken("Heading", "Desktop", "H5");
const headingDesktopH6 = resolveTypographyToken("Heading", "Desktop", "H6");
const bodyLargeRegular = resolveTypographyToken("Body", "Large", "Regular");
const bodyLargeMedium = resolveTypographyToken("Body", "Large", "Medium");
const bodyBaseRegular = resolveTypographyToken("Body", "Base", "Regular");
const bodyBaseMedium = resolveTypographyToken("Body", "Base", "Medium");
const bodySmallRegular = resolveTypographyToken("Body", "Small", "Regular");
const bodySmallMedium = resolveTypographyToken("Body", "Small", "Medium");

const typographyTokens = {
  displayPrimary,
  displayLarge: displayPrimary,
  displaySmall: headingDesktopH1,
  headingLarge: headingDesktopH2,
  headingMedium: headingDesktopH3,
  headingSmall: bodyLargeMedium,
  headingDesktopH1,
  headingDesktopH2,
  headingDesktopH3,
  headingDesktopH4,
  headingDesktopH5,
  headingDesktopH6,
  bodyLargeRegular,
  bodyLargeMedium,
  bodyBaseRegular,
  bodyBaseMedium,
  bodyRegular: bodyBaseRegular,
  bodyMedium: bodyBaseMedium,
  bodySmallRegular,
  bodySmallMedium,
  smallRegular: bodySmallRegular,
  smallMedium: bodySmallMedium,
};

const fontFamilies = {
  primary: rawTypographyTokens.fontFamilies?.inter?.value || "Inter",
};

const fontStack = `${fontFamilies.primary}, 'Figtree', 'Source Sans Pro', sans-serif`;

const mapFontWeight = (weight) => {
  if (!weight) return 400;
  const numericValue = Number(weight);
  if (!Number.isNaN(numericValue)) return numericValue;
  const normalized = weight.toString().toLowerCase();
  if (normalized.includes("semi")) return 600;
  if (normalized.includes("medium")) return 500;
  if (normalized.includes("bold")) return 700;
  return 400;
};

const parseFontSize = (size) => {
  if (typeof size === "number") return size;
  if (typeof size === "string") return parseFloat(size);
  return undefined;
};

const colorTokensRaw = {
  backgroundPrimary: resolveColorToken("Color Tokens", "Background", "Primary"),
  backgroundSecondary: resolveColorToken(
    "Color Tokens",
    "Background",
    "Secondary"
  ),
  backgroundTertiary: resolveColorToken(
    "Color Tokens",
    "Background",
    "Tertiary"
  ),
  borderPrimary: resolveColorToken("Color Tokens", "Border", "Primary"),
  borderSecondary: resolveColorToken("Color Tokens", "Border", "Secondary"),
  contentDarkPrimary: resolveColorToken(
    "Color Tokens",
    "Content",
    "Dark",
    "Primary"
  ),
  contentDarkTertiary: resolveColorToken(
    "Color Tokens",
    "Content",
    "Dark",
    "Tertiary"
  ),
  contentDarkSecondary: resolveColorToken(
    "Color Tokens",
    "Content",
    "Dark",
    "Secondary"
  ),
  contentDarkInformative: resolveColorToken(
    "Color Tokens",
    "Content",
    "Dark",
    "Informative"
  ),
  contentDarkPositive: resolveColorToken(
    "Color Tokens",
    "Content",
    "Dark",
    "Positive"
  ),
  contentDarkDisable: resolveColorToken(
    "Color Tokens",
    "Content",
    "Dark",
    "Disable"
  ),
  contentLightPrimary: resolveColorToken(
    "Color Tokens",
    "Content",
    "Light",
    "Primary"
  ),
  contentLightDisable: resolveColorToken(
    "Color Tokens",
    "Content",
    "Light",
    "Disable"
  ),
  interactionBlueBase: resolveColorToken(
    "Color Tokens",
    "Interaction",
    "Blue",
    "Base"
  ),
  interactionBlueHover: resolveColorToken(
    "Color Tokens",
    "Interaction",
    "Blue",
    "Hover"
  ),
  interactionBlueActive: resolveColorToken(
    "Color Tokens",
    "Interaction",
    "Blue",
    "Active"
  ),
  interactionPurpleBase: resolveColorToken(
    "Color Tokens",
    "Interaction",
    "Purple",
    "Base"
  ),
  interactionPurpleHover: resolveColorToken(
    "Color Tokens",
    "Interaction",
    "Purple",
    "Hover"
  ),
  interactionPurpleActive: resolveColorToken(
    "Color Tokens",
    "Interaction",
    "Purple",
    "Active"
  ),
  interactionGreenBase: resolveColorToken(
    "Color Tokens",
    "Interaction",
    "Green",
    "Base"
  ),
  interactionGreenHover: resolveColorToken(
    "Color Tokens",
    "Interaction",
    "Green",
    "Hover"
  ),
  interactionGreenActive: resolveColorToken(
    "Color Tokens",
    "Interaction",
    "Green",
    "Active"
  ),
  interactionOutlineBase: resolveColorToken(
    "Color Tokens",
    "Interaction",
    "Outline",
    "Base"
  ),
  actionOutlineBase: resolveColorToken(
    "Color Tokens",
    "Action",
    "Outline",
    "Base"
  ),
  actionPrimaryBase: resolveColorToken(
    "Color Tokens",
    "Action",
    "Primary",
    "Base"
  ),
  actionPrimaryActive: resolveColorToken(
    "Color Tokens",
    "Action",
    "Primary",
    "Active"
  ),
  actionSecondarySelected: resolveColorToken(
    "Color Tokens",
    "Action",
    "Secondary",
    "Selected"
  ),
  actionSecondaryActive: resolveColorToken(
    "Color Tokens",
    "Action",
    "Secondary",
    "Active"
  ),
  actionSecondaryHover: resolveColorToken(
    "Color Tokens",
    "Action",
    "Secondary",
    "Hover"
  ),
  actionSecondaryBase2: resolveColorToken(
    "Color Tokens",
    "Action",
    "Secondary",
    "Base 2"
  ),
};

const colorFallbacks = {
  backgroundPrimary: "#ffffff",
  backgroundSecondary: "#f5f7fb",
  backgroundTertiary: "#f2f2f2",
  borderPrimary: "#e2e8f0",
  borderSecondary: "#cbd5f5",
  contentDarkPrimary: "#000000",
  contentDarkTertiary: "#afafaf",
  contentDarkSecondary: "#727272",
  contentDarkInformative: "#2563eb",
  contentDarkPositive: "#22c55e",
  contentDarkDisable: "#98a2b3",
  contentLightPrimary: "#ffffff",
  contentLightDisable: "#98a2b3",
  interactionBlueBase: "#2563eb",
  interactionBlueHover: "#1d4ed8",
  interactionBlueActive: "#1e40af",
  interactionPurpleBase: "#954baf",
  interactionPurpleHover: "#773c8c",
  interactionPurpleActive: "#592d69",
  interactionGreenBase: "#589e67",
  interactionGreenHover: "#467e52",
  interactionGreenActive: "#355f3e",
  interactionOutlineBase: "#afafaf",
  actionOutlineBase: "#000000",
  actionPrimaryBase: "#000000",
  actionPrimaryActive: "#4b4b4b",
  actionSecondaryActive: "#e4e4e4",
  actionSecondaryHover: "#e8e8e8",
  actionSecondaryBase2: "#f2f2f2",
  actionSecondarySelected: "#d8d8d8",
};

const resolveColorValue = (value) => {
  if (!value) return undefined;
  if (typeof value === "string" && value.startsWith("{") && value.endsWith("}")) {
    const path = value.slice(1, -1).split(".");
    if (path[0] === "GlobalColors") {
      const [, family, shade] = path;
      return globalColorPalette[family]?.[shade];
    }
    return resolveValue(value, rawColorTokens);
  }
  return value;
};

const colorTokens = Object.fromEntries(
  Object.entries(colorTokensRaw).map(([key, value]) => {
    const resolved = resolveColorValue(value) || colorFallbacks[key] || "#f5f5f5";
    return [key, resolved];
  })
);

export const pxValue = (value) => (typeof value === "number" ? `${value}px` : value);

export {
  typographyTokens,
  colorTokens,
  fontFamilies,
  fontStack,
  mapFontWeight,
  parseFontSize,
};
